import { Platform } from "react-native";
import { File, Paths } from "expo-file-system";
import * as Sharing from "expo-sharing";

let isSharingInProgress = false;

/**
 * Initiates a file download for a media item.
 * - On Web: Uses the standard browser download process (creates an anchor element with download attribute
 *   and simulates a click, fetching blob to guarantee direct save prompts with clean filenames).
 * - On Native (iOS/Android): Downloads the file to the app cache directory and triggers the device share/save dialog.
 *
 * @param {string} url - The URL of the media file to download.
 * @param {string} [fileName] - Optional filename for the downloaded file.
 * @returns {Promise<boolean>} Resolves to true if download was successfully initiated, false otherwise.
 */
export async function downloadMediaFile(url: string, fileName?: string): Promise<boolean> {
  if (!url) return false;

  const targetFileName = fileName || url.split("/").pop()?.split("?")[0] || "download";

  if (Platform.OS === "web") {
    try {
      if (typeof document !== "undefined") {
        // Attempt blob fetch to guarantee browser save dialog with targetFileName
        const response = await fetch(url);
        if (response.ok) {
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = targetFileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
          return true;
        }
      }
    } catch {
      // Fallback for CORS restricted origins: standard anchor download
      if (typeof document !== "undefined") {
        const link = document.createElement("a");
        link.href = url;
        link.download = targetFileName;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return true;
      }
    }
    return false;
  }

  // Native mobile platforms (iOS / Android)
  if (isSharingInProgress) {
    console.warn("[MediaDownload] Share already in progress, ignoring concurrent tap.");
    return false;
  }

  isSharingInProgress = true;
  try {
    const targetFile = new File(Paths.cache, targetFileName);
    const downloaded = await File.downloadFileAsync(url, targetFile, { idempotent: true });
    if (await Sharing.isAvailableAsync()) {
      await Sharing.shareAsync(downloaded.uri);
      return true;
    }
  } catch (error: any) {
    const errorStr = String(error?.code || error?.message || error || "");
    if (errorStr.includes("ERR_SHARING_IN_PROGRESS") || errorStr.includes("Another share request is being processed")) {
      console.warn("[MediaDownload] Another share request is already in progress.");
    } else {
      console.error("Failed to download media file on mobile device:", error);
    }
  } finally {
    isSharingInProgress = false;
  }

  return false;
}
